import clsx from "clsx";
import { Link, useLocation } from "react-router";
import { Component, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";

import { useTenant } from "@/hooks/useTenant";
import { trackEvent } from "@/lib/mixpanel";
import { NAV_LINKS } from "@/utils/constants";
import { UserMenu } from "../auth/UserMenu";

const DEFAULT_BRAND = "Sovrun";

/**
 * Error boundary to prevent UserMenu crashes from taking down the whole page.
 * BetterAuth org hooks can throw for users without an active org.
 */
class UserMenuErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("UserMenu error boundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <button
          onClick={() => this.setState({ hasError: false })}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-500 transition hover:bg-slate-50"
        >
          Retry
        </button>
      );
    }
    return this.props.children;
  }
}

export const Header = () => {
  const pathname = useLocation().pathname;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { displayName, logoUrl } = useTenant();
  const brandName = displayName ?? DEFAULT_BRAND;
  const brandInitial = brandName.charAt(0).toUpperCase() || "S";

  // Homepage has its own header
  if (pathname === "/") return null;

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname?.startsWith(href);
  };

  const handleNavClick = (label: string, href: string) => {
    trackEvent("Navigation Clicked", {
      link_name: label,
      from_page: pathname || "/",
      to_page: href,
    });
    setIsMobileOpen(false);
  };

  // Tenant white-label surface: TenantThemeProvider injects --tenant-background
  // / --tenant-foreground onto :root only when a tenant theme is resolved
  // (SFWL-002). The `var(--x, fallback)` form means the header keeps its
  // current light look untouched on bare/default localhost (no tenant theme
  // -> the CSS variable is unset -> the fallback wins) while a themed tenant
  // (e.g. Sweet Futures' deep navy #0B1D3A) recolors the surface + text
  // without a Sweet-specific hardcode.
  const headerSurfaceStyle = {
    backgroundColor: "var(--tenant-background, rgba(255, 255, 255, 0.8))",
    borderColor: "var(--tenant-background, #e2e8f0)",
    color: "var(--tenant-foreground, #0f172a)",
  } as const;

  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur"
      style={headerSurfaceStyle}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:py-4">
        <div className="flex items-center gap-3">
          <Link to="/strategies" className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${brandName} logo`}
                className="h-9 w-auto object-contain"
              />
            ) : (
              <div className="h-9 w-9 rounded-xl bg-slate-900 text-center text-lg font-black leading-9 text-white shadow-sm">
                {brandInitial}
              </div>
            )}
            {!logoUrl && (
              <p className="text-sm font-bold uppercase tracking-widest text-[color:var(--tenant-foreground,#0f172a)]">
                {brandName}
              </p>
            )}
          </Link>
          <nav className="ml-8 hidden items-center gap-6 text-sm font-semibold text-[color:var(--tenant-foreground,#334155)] md:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => handleNavClick(link.label, link.href)}
                className={clsx(
                  "rounded-full px-3 py-2 transition",
                  isActive(link.href)
                    ? "bg-slate-900 text-white shadow-sm"
                    : "hover:bg-slate-100"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex md:items-center md:gap-3">
            <UserMenuErrorBoundary>
              <UserMenu />
            </UserMenuErrorBoundary>
          </div>
          <button
            onClick={() => setIsMobileOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--tenant-background,#e2e8f0)] text-[color:var(--tenant-foreground,#1e293b)] transition hover:bg-slate-50 md:hidden"
            aria-label="Toggle navigation menu"
          >
            {isMobileOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {isMobileOpen ? (
        <div
          className="border-t px-4 pb-4 md:hidden"
          style={headerSurfaceStyle}
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-2 py-3 text-sm font-semibold text-[color:var(--tenant-foreground,#1e293b)]">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => handleNavClick(link.label, link.href)}
                className={clsx(
                  "rounded-lg px-3 py-2",
                  isActive(link.href) ? "bg-slate-900 text-white" : "hover:bg-slate-50"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-2">
            <UserMenuErrorBoundary>
              <UserMenu />
            </UserMenuErrorBoundary>
          </div>
        </div>
      ) : null}
    </header>
  );
};
